import type { DocumentContent } from '@/types/documents'

const CJK_RE = /[\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff]/
const SENTENCE_END_RE = /[。！？!?]+["'”’）)]?|\.(?=\s|$)/u

export function hasChinese(text: string) {
  return CJK_RE.test(text)
}

export function normalizeReaderWhitespace(text: string) {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .replace(/\u00a0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{4,}/g, '\n\n\n')
    .trim()
}

export function removeIsolatedPageNumbers(text: string) {
  return text
    .split('\n')
    .filter(
      (line) => !/^\s*(?:第\s*)?\d{1,4}\s*(?:页|\/\s*\d{1,4})?\s*$/.test(line)
    )
    .join('\n')
}

export function removeRepeatedEdgeLines(pages: string[]) {
  if (pages.length < 3) return pages

  const firstCounts = new Map<string, number>()
  const lastCounts = new Map<string, number>()

  for (const page of pages) {
    const lines = normalizeReaderWhitespace(page)
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)

    const first = lines[0]
    const last = lines[lines.length - 1]
    if (first && first.length <= 80) {
      firstCounts.set(first, (firstCounts.get(first) ?? 0) + 1)
    }
    if (last && last.length <= 80) {
      lastCounts.set(last, (lastCounts.get(last) ?? 0) + 1)
    }
  }

  const threshold = Math.max(3, Math.ceil(pages.length * 0.45))
  const repeated = new Set<string>()

  for (const [line, count] of [...firstCounts, ...lastCounts]) {
    if (count >= threshold) repeated.add(line)
  }

  if (repeated.size === 0) return pages

  return pages.map((page) =>
    page
      .split('\n')
      .filter((line) => !repeated.has(line.trim()))
      .join('\n')
  )
}

export function mergeBrokenPdfLines(text: string) {
  const lines = text.split('\n')
  const merged: string[] = []

  for (const rawLine of lines) {
    const line = rawLine.trim()
    if (!line) {
      merged.push('')
      continue
    }

    const previous = merged[merged.length - 1]
    const previousLooksOpen =
      previous &&
      !SENTENCE_END_RE.test(previous) &&
      !/[：:；;]$/.test(previous) &&
      !/^[「『"“‘（(]/.test(line) &&
      !/^[\-•*]/.test(line)

    if (previousLooksOpen) {
      const spacer = hasChinese(previous) && hasChinese(line) ? '' : ' '
      merged[merged.length - 1] = `${previous}${spacer}${line}`
    } else {
      merged.push(line)
    }
  }

  return merged.join('\n')
}

export function cleanReaderText(text: string) {
  return mergeBrokenPdfLines(
    removeIsolatedPageNumbers(normalizeReaderWhitespace(text))
  )
}

export function cleanPdfPageTexts(pages: string[]) {
  return removeRepeatedEdgeLines(pages).map(cleanReaderText)
}

export function splitParagraphs(text: string) {
  return normalizeReaderWhitespace(text)
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
}

export function splitSentences(text: string) {
  const normalized = normalizeReaderWhitespace(text)
  const sentences: string[] = []
  let buffer = ''

  for (const char of normalized) {
    buffer += char
    if (SENTENCE_END_RE.test(char)) {
      const sentence = buffer.trim()
      if (sentence) sentences.push(sentence)
      buffer = ''
    }
  }

  const remainder = buffer.trim()
  if (remainder) sentences.push(remainder)

  return sentences.length > 0 ? sentences : normalized ? [normalized] : []
}

export function documentContentToText(content: DocumentContent) {
  return content.paragraphs
    .map((paragraph) =>
      paragraph.sentences.map((sentence) => sentence.text).join('')
    )
    .join('\n\n')
}
